import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Prescription,
  PrescriptionExt,
  Fulfillment,
  DrawerType,
  IAdminUser,
  RxDrug
} from '@care-portals/api-interfaces';
import { AuthService } from '../../../services/auth/auth.service';
import { DrawerStateFacade } from '../../../+store/drawer-state/drawer-state.facade';
import { PrescriptionFacade } from '../../../+store/prescription/prescription.facade';
import { OrdersFacade } from '../../../+store/order/order.facade';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationModule } from 'ng-zorro-antd/notification';

@Component({
  selector: 'my-health-admin-order-prescriptions',
  templateUrl: './order-prescriptions.component.html',
  styleUrls: ['./order-prescriptions.component.less'],
  standalone: true,
  imports: [
    NzLayoutModule,
    NzTableModule,
    NzDividerModule,
    NzPopconfirmModule,
    CommonModule,
    NzButtonModule,
    NzToolTipModule,
    NzModalModule,
    NzNotificationModule
  ]
})
export class OrderPrescriptionsComponent implements OnInit, OnDestroy {
  tableData: PrescriptionExt[];
  private isDestroyed = new Subject();
  sendRxCooldowns = new Set<string>();
  sendingPdfToChat = new Set<string>();
  pendingRxToLink: Prescription | null = null;
  pendingTransferAfterLink = false;
  isLoading: boolean;
  prescriptions: Prescription[];
  orderId;

  constructor(
    private readonly drawerFacade: DrawerStateFacade,
    private readonly ordersFacade: OrdersFacade,
    private readonly prescriptionFacade: PrescriptionFacade,
    public readonly router: Router,
    public readonly auth: AuthService,
    private readonly modalService: NzModalService,
    private readonly notification: NzNotificationService
  ) {
    this.ordersFacade.selectedId$.subscribe(id => (this.orderId = id));

    this.drawerFacade.popped$.subscribe(res => {
      if (res?.props === true || res?.name === DrawerType.FIND_ORDER) {
        if (res?.name === DrawerType.CREATE_FULFILL) {
          this.updateLastFulfill();
        } else if (
          res?.name === DrawerType.CREATE_RX ||
          res?.name === DrawerType.EDIT_RX
        ) {
          this.updateLastFulfill();
          if (this.orderId) {
            this.ordersFacade.loadOrder(this.orderId);
          }
        } else if (res?.name === DrawerType.FIND_ORDER && this.pendingRxToLink) {
          const rxToLink = this.pendingRxToLink;
          const shouldTransferAfterLink = this.pendingTransferAfterLink;
          this.pendingRxToLink = null;
          this.pendingTransferAfterLink = false;

          const orderId = res?.props;
          if (Number.isFinite(orderId) && rxToLink?._id) {
            this.prescriptionFacade.updatePrescription(rxToLink._id, {
              linkedOrderId: orderId
            });
            this.notification.success(
              'Linked',
              `RX ${rxToLink._id} was linked to order #${orderId}.`
            );
            if (shouldTransferAfterLink) {
              this.sendPrescriptionPdfToChat(rxToLink._id);
            }
          }
        }
      }
    });

    this.isLoading = true;
    this.ordersFacade.activeOrderRxResults$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(rxResults => {
        this.prescriptions = rxResults?.prescriptions || [];
        this.updateLastFulfill();
        this.isLoading = false;
      });

    this.prescriptionFacade.sendPrescriptionPdfToChatCompleted$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(({ rxId }) => this.sendingPdfToChat.delete(rxId));
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }

  updateLastFulfill() {
    if (!this.prescriptions) {
      return;
    }

    this.tableData = this.prescriptions.map(item => {
      const lastFulfill =
        item.fulfillments && item.fulfillments.length > 0
          ? { ...item.fulfillments[item.fulfillments.length - 1] }
          : ({} as Fulfillment);

      const rx: PrescriptionExt = { ...item, lastFulfill, expand: false };
      return rx;
    });
  }

  sendRx(rxId: string) {
    this.sendRxCooldowns.add(rxId);
    setTimeout(() => this.sendRxCooldowns.delete(rxId), 60000);
    this.prescriptionFacade.sendRx(rxId);
  }

  sendPrescriptionPdfToChat(rxId: string) {
    this.sendingPdfToChat.add(rxId);
    this.prescriptionFacade.sendPrescriptionPdfToPatientChat(
      rxId,
      this.orderId
    );
  }

  transferRx(rx: Prescription) {
    this.modalService.confirm({
      nzTitle: 'Transfer RX',
      nzContent:
        'Sending this RX will transfer it out. Do you want to link an order before transferring?',
      nzKeyboard: false,
      nzOkText: 'Transfer Only',
      nzCancelText: 'Link Order & Transfer',
      nzOnOk: () => {
        this.sendPrescriptionPdfToChat(rx._id);
      },
      nzOnCancel: () => {
        if (this.orderId) {
          this.prescriptionFacade.updatePrescription(rx._id, {
            linkedOrderId: this.orderId.toString()
          });
          this.sendPrescriptionPdfToChat(rx._id);
          return;
        }
        const customerId =
          typeof rx.customer === 'string' ? rx.customer : rx.customer?._id;
        if (!customerId) {
          this.notification.error('Error', 'Unable to determine patient for RX.');
          return;
        }
        this.pendingRxToLink = rx;
        this.pendingTransferAfterLink = true;
        this.drawerFacade.openDrawer({
          name: DrawerType.FIND_ORDER,
          props: { customerId }
        });
      }
    });
  }

  isOnCooldown(rxId: string): boolean {
    return this.sendRxCooldowns.has(rxId);
  }

  openDuplicateRx(rx: Prescription) {
    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_RX,
      props: { customer: rx.customer, rx }
    });
  }

  openEditRx(rx: Prescription) {
    this.drawerFacade.openDrawer({
      name: DrawerType.EDIT_RX,
      props: { rx }
    });
  }

  voidRx(rxId: string): void {
    this.prescriptionFacade.updatePrescription(rxId, { voided: true });
  }

  openFulfillment(rx: Prescription) {
    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_FULFILL,
      props: { rx: { ...rx } }
    });
  }

  changeStatus(status, prescription: PrescriptionExt) {
    prescription.lastFulfill = { ...prescription.lastFulfill, status };
    const len = prescription.fulfillments.length;
    const newFulfillments = [...prescription.fulfillments];
    newFulfillments[len - 1] = prescription.lastFulfill;

    this.prescriptionFacade.updatePrescription(prescription._id, {
      fulfillments: newFulfillments
    });
  }

  asAdminUser(data) {
    return data as IAdminUser;
  }

  viewRx(rx: Prescription) {
    this.drawerFacade.openDrawer({
      name: DrawerType.PRESCRIPTION_PDF,
      props: {
        rxId: rx._id
      }
    });
  }

  openOrderDetails(orderId: string) {
    this.ordersFacade.openOrderDetails(orderId);
  }

  onDrugClick(drug: RxDrug) {
    this.drawerFacade.openDrawer({
      name: DrawerType.DRUG_DETAIL,
      props: { din: drug.din }
    });
  }
}
